
import { useState, useEffect, useRef } from 'react';

interface InlineEditProps {
  value: string;
  onSave: (v: string) => void;
  multiline?: boolean;
  className?: string;
  placeholder?: string;
  rows?: number;
}

export default function InlineEdit({
  value,
  onSave,
  multiline = false,
  className = '',
  placeholder = 'Click to edit...',
  rows = 3,
}: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { setDraft(value); }, [value]);

  useEffect(() => {
    if (editing) {
      if (multiline && textareaRef.current) textareaRef.current.focus();
      else if (!multiline && inputRef.current) inputRef.current.focus();
    }
  }, [editing, multiline]);

  const commit = () => {
    onSave(draft.trim() || value);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  if (editing) {
    return multiline ? (
      <textarea
        ref={textareaRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Escape') cancel(); }}
        placeholder={placeholder}
        rows={rows}
        className={`bg-white/10 border border-electric-blue/80 rounded-lg p-2 text-white font-inter resize-none w-full outline-none text-sm leading-relaxed ${className}`}
      />
    ) : (
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
        placeholder={placeholder}
        className={`bg-white/10 border border-electric-blue/80 rounded-lg px-2 py-1 text-white font-inter outline-none w-full text-sm ${className}`}
      />
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      title="Click to edit"
      className={`cursor-pointer group relative inline-flex items-center gap-1 hover:bg-white/5 hover:outline hover:outline-1 hover:outline-electric-blue/50 rounded px-0.5 transition-all duration-200 ${className}`}
    >
      <span>{value || <span className="text-white/30 italic">{placeholder}</span>}</span>
      <i className="ri-pencil-line text-electric-blue text-xs opacity-0 group-hover:opacity-100 transition-opacity w-3 h-3 flex items-center justify-center flex-shrink-0"></i>
    </span>
  );
}
